/**
 * Templates AG Grid Component
 *
 * Clean, simple grid view with:
 * - Alternating row colors
 * - Plain text (no inline text boxes)
 * - Quick filter
 * - Column visibility toggle
 * - Filter dropdown
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Spin } from 'antd';
import type {
  ColDef,
  GridReadyEvent,
  ICellRendererParams,
  GridApi,
  RowClickedEvent,
} from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import './blueprint-grid.css';

import type { ProcessTemplate, RecurrenceRule, Category } from '@/types';
import type { TemplateWithStats as ApiTemplateWithStats } from '@api/templates';
import { useTheme } from '@contexts/ThemeContext';
import GridToolbar, { ColumnConfig, FilterConfig, ActiveFilter } from './GridToolbar';

interface TemplatesGridProps {
  templates: ApiTemplateWithStats[];
  loading: boolean;
  onSelectTemplate: (template: ProcessTemplate) => void;
  categories?: Category[];
  extraToolbarButtons?: React.ReactNode;
  initialFilters?: ActiveFilter[]; // For loading saved views
}

const formatRecurrence = (rule: RecurrenceRule): string => {
  if (rule.type === 'interval') {
    const unit = rule.unit === 'day' ? 'day' : rule.unit === 'week' ? 'week' : rule.unit === 'month' ? 'month' : 'year';
    return `Every ${rule.every} ${unit}${rule.every > 1 ? 's' : ''}`;
  } else if (rule.type === 'calendar') {
    if (rule.pattern === 'monthly') {
      if (rule.dayOfMonth) {
        return `Monthly on day ${rule.dayOfMonth}`;
      } else {
        const week = rule.week === -1 ? 'Last' : `${rule.week}${['st', 'nd', 'rd', 'th'][Math.min(rule.week! - 1, 3)]}`;
        return `Monthly on ${week} ${rule.dayOfWeek}`;
      }
    } else {
      const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `Yearly on ${months[rule.month!]} ${rule.day}`;
    }
  } else {
    // type === 'once'
    return `Once on ${rule.date}`;
  }
};

// Simple text renderers - no fancy components
const CategoryRenderer = (props: ICellRendererParams<ProcessTemplate>) => {
  const category = props.data?.category;
  if (!category) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
  return (
    <span className="bp-tag-chip">
      <span
        className="bp-tag-dot"
        style={{ backgroundColor: category.color || 'var(--text-tertiary)' }}
      />
      {category.name}
    </span>
  );
};

const TagsRenderer = (props: ICellRendererParams<ProcessTemplate>) => {
  const tags = props.data?.tags || [];
  if (tags.length === 0) return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
      {tags.slice(0, 3).map((tag) => (
        <span key={tag.id} className="bp-tag-chip">
          <span
            className="bp-tag-dot"
            style={{ backgroundColor: tag.color || 'var(--text-tertiary)' }}
          />
          {tag.name}
        </span>
      ))}
      {tags.length > 3 && (
        <span style={{ color: 'var(--text-tertiary)', fontSize: 11, fontFamily: 'var(--font-mono)' }}>
          +{tags.length - 3}
        </span>
      )}
    </div>
  );
};

const RecurrenceRenderer = (props: ICellRendererParams<ProcessTemplate>) => {
  const rule = props.data?.recurrenceRule;
  if (!rule) return null;
  return <span className="bp-mono" style={{ color: 'var(--text-secondary)' }}>{formatRecurrence(rule)}</span>;
};

const StatsRenderer = (props: ICellRendererParams<ApiTemplateWithStats>) => {
  const template = props.data;
  if (!template) return null;

  return (
    <span className="bp-mono" style={{ letterSpacing: '0.03em' }}>
      <span style={{ color: 'var(--status-info)' }}>{template.pendingCount}p</span>
      {' · '}
      <span style={{ color: 'var(--status-success)' }}>{template.completedCount}c</span>
      {template.overdueCount > 0 && (
        <>
          {' · '}
          <span style={{ color: 'var(--status-error)' }}>{template.overdueCount}!</span>
        </>
      )}
    </span>
  );
};

export default function TemplatesGrid({
  templates,
  loading,
  onSelectTemplate,
  categories = [],
  extraToolbarButtons,
  initialFilters,
}: TemplatesGridProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark ?? false;
  const gridRef = useRef<AgGridReact>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>(initialFilters || []);

  // Apply initial filters from saved view (when URL params change)
  useEffect(() => {
    if (initialFilters && initialFilters.length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [initialFilters]);

  // Column configuration for toolbar
  const columnConfig: ColumnConfig[] = useMemo(() => [
    { field: 'title', headerName: 'Name', alwaysVisible: true },
    { field: 'category', headerName: 'Category' },
    { field: 'recurrenceRule', headerName: 'Schedule' },
    { field: 'tags', headerName: 'Tags' },
    { field: 'defaultAssignee', headerName: 'Assignee' },
    { field: 'stats', headerName: 'Stats' },
    { field: 'isActive', headerName: 'Active' },
  ], []);

  // Filter configuration
  const filterConfig: FilterConfig[] = useMemo(() => [
    {
      field: 'category',
      label: 'Category',
      type: 'select',
      options: categories.map(cat => ({ value: cat.id, label: cat.name })),
    },
    {
      field: 'isActive',
      label: 'Status',
      type: 'select',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ], [categories]);

  // Filter templates based on active filters
  const filteredTemplates = useMemo(() => {
    if (activeFilters.length === 0) return templates;
    return templates.filter(template => {
      return activeFilters.every(filter => {
        if (filter.field === 'category') {
          return template.category?.id === filter.value;
        }
        if (filter.field === 'isActive') {
          return String(template.isActive) === filter.value;
        }
        return true;
      });
    });
  }, [templates, activeFilters]);

  const columnDefs: ColDef<ApiTemplateWithStats>[] = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Name',
        minWidth: 200,
        flex: 2,
        cellRenderer: (props: ICellRendererParams<ApiTemplateWithStats>) => (
          <span style={{ fontWeight: 500, color: 'var(--status-info)' }}>
            {props.value}
          </span>
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 130,
        cellRenderer: CategoryRenderer,
        valueGetter: (params) => params.data?.category?.name || '',
      },
      {
        field: 'recurrenceRule',
        headerName: 'Schedule',
        width: 180,
        cellRenderer: RecurrenceRenderer,
        valueGetter: (params) =>
          params.data?.recurrenceRule ? formatRecurrence(params.data.recurrenceRule) : '',
      },
      {
        field: 'tags',
        headerName: 'Tags',
        width: 180,
        cellRenderer: TagsRenderer,
        valueGetter: (params) => params.data?.tags?.map((t) => t.name).join(', ') || '',
      },
      {
        field: 'defaultAssignee',
        headerName: 'Assignee',
        width: 130,
        valueGetter: (params) => params.data?.defaultAssignee?.fullName || '—',
        cellStyle: (params) => params.value === '—' ? { color: 'var(--text-tertiary)' } : null,
      },
      {
        colId: 'stats',
        headerName: 'Stats',
        width: 100,
        cellRenderer: StatsRenderer,
        sortable: false,
      },
      {
        field: 'isActive',
        headerName: 'Active',
        width: 80,
        cellRenderer: (props: ICellRendererParams) => (
          <span className={`bp-status-chip ${props.value ? 'bp-status-active' : 'bp-status-inactive'}`}>
            {props.value ? 'ON' : 'OFF'}
          </span>
        ),
      },
    ],
    []
  );

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      // No floating filters - cleaner look
    }),
    []
  );

  const onGridReady = useCallback((params: GridReadyEvent) => {
    setGridApi(params.api);
    params.api.sizeColumnsToFit();
  }, []);

  const onRowClicked = useCallback((event: RowClickedEvent<ApiTemplateWithStats>) => {
    if (event.data) {
      onSelectTemplate(event.data);
    }
  }, [onSelectTemplate]);

  // Show loading overlay
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 300,
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar with search, columns, and filters */}
      <GridToolbar
        gridApi={gridApi}
        columns={columnConfig}
        filters={filterConfig}
        storageKey="templates-grid"
        searchPlaceholder="Search templates..."
        onSearchChange={setQuickFilterText}
        onFilterChange={setActiveFilters}
        quickFilterText={quickFilterText}
        extraButtons={extraToolbarButtons}
      />

      {/* Grid with custom styling */}
      <div
        className={`${isDark ? 'ag-theme-alpine-dark' : 'ag-theme-alpine'} bp-grid`}
        style={{
          flex: 1,
          minHeight: 400,
          ...({
            '--ag-background-color': 'var(--surface-1)',
            '--ag-odd-row-background-color': 'var(--row-alt, #f7f9fc)',
            '--ag-row-border-color': 'var(--border-subtle)',
            '--ag-header-background-color': 'var(--surface-1)',
            '--ag-border-color': 'var(--grid-line, #dde3ec)',
            '--ag-foreground-color': 'var(--text-primary)',
            '--ag-secondary-foreground-color': 'var(--text-secondary)',
            '--ag-row-hover-color': 'var(--hover-bg)',
            '--ag-selected-row-background-color': 'rgba(30, 64, 175, 0.10)',
            '--ag-header-foreground-color': 'var(--text-tertiary)',
          }),
        } as React.CSSProperties}
      >
        <AgGridReact
          ref={gridRef}
          rowData={filteredTemplates}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          onRowClicked={onRowClicked}
          quickFilterText={quickFilterText}
          animateRows
          rowSelection="single"
          suppressCellFocus
          getRowId={(params) => params.data.id}
          domLayout="autoHeight"
          rowHeight={40}
          headerHeight={36}
        />
      </div>

      {/* Row cursor — kept inline to avoid global pollution */}
      <style>{`
        .bp-grid .ag-row { cursor: pointer; }
      `}</style>
    </div>
  );
}
